import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const rolesService = {
  /**
   * Get all roles with permissions
   * @param {Object} filters - { active_only, search }
   */
  getRoles: async (filters = {}) => {
    try {
      const response = await apiClient.get('/roles', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  },

  /**
   * Get single role by ID
   * @param {number} id - Role ID
   */
  getRole: async (id) => {
    try {
      const response = await apiClient.get(`/roles/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching role:', error);
      throw error;
    }
  },

  /**
   * Create new role
   * @param {Object} roleData - Role data
   */
  createRole: async (roleData) => {
    try {
      const response = await apiClient.post('/roles', roleData);
      return response.data;
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  },

  /**
   * Update existing role
   * @param {number} id - Role ID
   * @param {Object} roleData - Updated role data
   */
  updateRole: async (id, roleData) => {
    try {
      const response = await apiClient.put(`/roles/${id}`, roleData);
      return response.data;
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  },

  /**
   * Delete role
   * @param {number} id - Role ID
   */
  deleteRole: async (id) => {
    try {
      const response = await apiClient.delete(`/roles/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  },

  /**
   * Assign permissions to role
   * @param {number} id - Role ID
   * @param {Array} permissionIds - Array of permission IDs
   */
  assignPermissions: async (id, permissionIds) => {
    try {
      const response = await apiClient.post(`/roles/${id}/permissions`, {
        permissions: permissionIds,
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning permissions:', error);
      throw error;
    }
  },

  /**
   * Get all permissions
   * @param {Object} filters - { module, search, group_by_module }
   */
  getPermissions: async (filters = {}) => {
    try {
      const response = await apiClient.get('/permissions', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error fetching permissions:', error);
      throw error;
    }
  },

  /**
   * Get all unique modules
   */
  getModules: async () => {
    try {
      const response = await apiClient.get('/permissions/modules');
      return response.data;
    } catch (error) {
      console.error('Error fetching modules:', error);
      throw error;
    }
  },

  /**
   * Get single permission by ID
   * @param {number} id - Permission ID
   */
  getPermission: async (id) => {
    try {
      const response = await apiClient.get(`/permissions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching permission:', error);
      throw error;
    }
  },

  /**
   * Create new permission
   * @param {Object} permissionData - Permission data
   */
  createPermission: async (permissionData) => {
    try {
      const response = await apiClient.post('/permissions', permissionData);
      return response.data;
    } catch (error) {
      console.error('Error creating permission:', error);
      throw error;
    }
  },

  /**
   * Update existing permission
   * @param {number} id - Permission ID
   * @param {Object} permissionData - Updated permission data
   */
  updatePermission: async (id, permissionData) => {
    try {
      const response = await apiClient.put(`/permissions/${id}`, permissionData);
      return response.data;
    } catch (error) {
      console.error('Error updating permission:', error);
      throw error;
    }
  },

  /**
   * Delete permission
   * @param {number} id - Permission ID
   */
  deletePermission: async (id) => {
    try {
      const response = await apiClient.delete(`/permissions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting permission:', error);
      throw error;
    }
  },
};

export default rolesService;
